/**
 * One contract for every client of GET /v1/fleet/vehicles. The API imports
 * this value too, so its validation cap cannot silently drift from clients.
 */
export const VEHICLE_LIST_PAGE_LIMIT = 100;

export {
  PERMISSIONS,
  ROLES,
  ROLE_PERMISSIONS,
  TENANT_ROLE_PERMISSIONS,
  PLATFORM_ROLE_PERMISSIONS,
} from "./permission-catalog.generated.js";

/** The only server-side sort for which fleet cursors are valid. */
export const VEHICLE_CURSOR_SORT = "createdAt.desc";

/** A corrupted cursor must never make a client request pages indefinitely. */
export const DEFAULT_MAX_CURSOR_PAGES = 50;

export interface CursorPage<T> {
  readonly items: readonly T[];
  readonly nextCursor: string | null;
}

export interface FetchAllPagesOptions<T> {
  readonly maxPages?: number;
  /** A full final page without a cursor is not an honest completion signal. */
  readonly pageSize?: number;
  /** Optional identity prevents a cursor-boundary duplicate reaching a UI. */
  readonly itemKey?: (item: T) => string;
}

export interface FetchAllPagesResult<T> {
  readonly items: readonly T[];
  /**
   * True only when pagination could not be completed because the server
   * repeated a cursor or the client-side safety bound was reached.
   * A null cursor is the API's authoritative end-of-list signal, including
   * when the final page happens to contain exactly the page limit.
   */
  readonly truncated: boolean;
  readonly pagesLoaded: number;
}

/**
 * Drains a cursor-paginated endpoint sequentially. It is deliberately free
 * of platform APIs so web, admin and Expo stores can supply their own fetcher
 * and retain their own retry/cancellation semantics.
 */
export async function fetchAllPages<T>(
  fetchPage: (cursor: string | undefined) => Promise<CursorPage<T>>,
  options: FetchAllPagesOptions<T> = {},
): Promise<FetchAllPagesResult<T>> {
  const maxPages = options.maxPages ?? DEFAULT_MAX_CURSOR_PAGES;
  const items: T[] = [];
  const seenCursors = new Set<string>();
  const seenItems = new Set<string>();
  let cursor: string | undefined;

  for (let pagesLoaded = 0; pagesLoaded < maxPages; pagesLoaded += 1) {
    const page = await fetchPage(cursor);
    for (const item of page.items) {
      const key = options.itemKey?.(item);
      if (key === undefined || !seenItems.has(key)) {
        if (key !== undefined) seenItems.add(key);
        items.push(item);
      }
    }

    if (page.nextCursor === null) {
      return {
        items,
        truncated: options.pageSize !== undefined && page.items.length >= options.pageSize,
        pagesLoaded: pagesLoaded + 1,
      };
    }

    if (seenCursors.has(page.nextCursor)) {
      return { items, truncated: true, pagesLoaded: pagesLoaded + 1 };
    }
    seenCursors.add(page.nextCursor);
    cursor = page.nextCursor;
  }

  return { items, truncated: true, pagesLoaded: maxPages };
}

export function listAllVehicles<T>(
  fetchPage: (cursor: string | undefined) => Promise<CursorPage<T>>,
  itemKey: (item: T) => string,
): Promise<FetchAllPagesResult<T>> {
  return fetchAllPages(fetchPage, { pageSize: VEHICLE_LIST_PAGE_LIMIT, itemKey });
}

export interface CappedListPage<T> {
  readonly items: readonly T[];
  /** The server's authoritative count over the whole collection, not just this page. */
  readonly total: number;
}

export interface CappedListResult<T> {
  readonly items: readonly T[];
  /** True when the server's `total` exceeds the items this capped page returned. */
  readonly truncated: boolean;
}

/**
 * Some list endpoints cap `limit` server-side and report a `total` instead of
 * a cursor (no `nextCursor` to drain). `fetchAllPages` does not apply there;
 * this gives those endpoints the same honest `truncated` signal without
 * inventing cursor support the API does not have.
 */
export function toCappedListResult<T>(page: CappedListPage<T>): CappedListResult<T> {
  return { items: page.items, truncated: page.items.length < page.total };
}
